import {
  buildOfferPropertySurroundingsCacheKey,
  fetchOfferPropertySurroundings,
} from 'actions/OfferActions'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyObject } from 'lib/object/objectUtils'
import { useContext, useEffect, useMemo } from 'react'
import type { AppDispatch } from 'src/client/store'
import {
  OFFER_PROPERTY_SURROUNDINGS_CATEGORIES,
  OFFER_PROPERTY_SURROUNDINGS_ENGLISH_ONLY,
  OFFER_PROPERTY_SURROUNDINGS_EXCLUDE_PRIMARY_TYPES,
} from 'tripPlanner/api'

const SURROUNDINGS_LIMIT = 10
const SURROUNDINGS_EXCLUDE_TYPES: Array<string> = [
  ...OFFER_PROPERTY_SURROUNDINGS_EXCLUDE_PRIMARY_TYPES,
]

interface Options {
  disabled?: boolean
  enabled?: boolean
}

interface OfferPropertySurroundingsState {
  surroundings?: App.OfferPropertySurroundings
  error?: string
  fetching: boolean
  hasFetched: boolean
}

interface Params {
  offerId?: string
}

function useOfferPropertySurroundings(
  params: Params,
  options: Options = EmptyObject,
): OfferPropertySurroundingsState {
  const dispatch: AppDispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)

  const requestOptions = useMemo(() => {
    return {
      categories: OFFER_PROPERTY_SURROUNDINGS_CATEGORIES,
      limit: SURROUNDINGS_LIMIT,
      region: currentRegionCode,
      excludeTypes: SURROUNDINGS_EXCLUDE_TYPES,
      englishOnly: OFFER_PROPERTY_SURROUNDINGS_ENGLISH_ONLY,
    }
  }, [currentRegionCode])

  const cacheKey = params.offerId && requestOptions ?
      buildOfferPropertySurroundingsCacheKey(params.offerId, requestOptions) :
    ''
  const entry = useAppSelector((state) => state.offer.offerPropertySurroundings[cacheKey])

  useEffect(() => {
    if (options.disabled || !options.enabled || !params.offerId || !requestOptions) {
      return
    }

    dispatch(fetchOfferPropertySurroundings(params.offerId, requestOptions))
  }, [dispatch, options.disabled, options.enabled, params.offerId, requestOptions])

  return {
    surroundings: entry?.surroundings,
    error: entry?.error,
    fetching: !!entry?.fetching,
    hasFetched: !!entry?.surroundings && !entry.fetching,
  }
}

export default useOfferPropertySurroundings
